import { reactive, computed } from '@nuxtjs/composition-api';
import { StateInterface, UseLandingInterface } from '~/composables/useLanding/useLanding';
import { PLANS_HORIZONTAL } from "~/utils/constants/landings";
import { DURATION_SPAN_MONTHLY } from '~/utils/constants/product';
import { GenericObjectType } from '~/utils/types';
import { Landing } from '~/utils/types/Landing';
import { PlanItem } from '~/utils/types/Plan';
import { toggleModule, setPaperAdded, setChoosenShippingMethod, removeModule, emptyModules,setAcceptedQuantities,setPaymentFrequencyChoosen, addModule, setSelectedShippingKey } from '~/utils/helpers/composables';

const state = reactive<StateInterface>({
  landing: null,
  plansLayout: PLANS_HORIZONTAL as string,
  paperAdded: [],
  choosenShippingMethod: {},
  selectedShippingKey: {},
  choosenModules: new Map(),
  modulesKeys: [], // We need this key map because the state can't watch complex objects as Map, using the keys as position-sku to make the sorting faster
  acceptedQuantities: 0,
  layoutSelectedPlan: null,
  selectedPlan : null,
  paymentFrequencyChoosen: null,
  triggerChangeHelper: false
});

/**
 * Global store for managing UI state.
 *
 * See the {@link UseUiStateInterface} for a list of methods and values available in this composable.
 */
export function useLanding(): UseLandingInterface {
  
  const setLayoutSelectedPlan = (val: number) => {
    state.layoutSelectedPlan = val;
    state.selectedPlan = state.landing.plans.items.find(plan => plan.id == val);
  };

  const getLowPaymentFrequency = ():string => {
    try {
      return state.selectedPlan['paymentFrequencies'][0]
    } catch (e) {
      console.log(`Error in useLandings > getLowPaymentFrequency, returning M as default: ${e}`)
      return DURATION_SPAN_MONTHLY;
    }
  }
  const getHighPaymentFrequency = ():string => {
    try {
      return state.selectedPlan.paymentFrequencies[1] ?? getLowPaymentFrequency();
    } catch (e) {
      console.log(`Error in useLandings > getHighPaymentFrequency, returning low frequency as default: ${e}`)
      return getLowPaymentFrequency()
    }
  }

  const setLanding = (val: Landing, inEvidenceSku?:string) => {
    state.landing = val;
    /** Init state */
    const preSelectedPlan = inEvidenceSku ? val.plans.items.find(el => el.sku==inEvidenceSku) : val.plans.items.find(el => !!el.preselected);
    const preSelectedPlanId = preSelectedPlan ? preSelectedPlan.id : val.plans.items[0].id;
    setLayoutSelectedPlan(preSelectedPlanId);
    setAcceptedQuantities(state, preSelectedPlan?.modulesInCartQty?.min),
    setPaymentFrequencyChoosen(state, getHighPaymentFrequency, getHighPaymentFrequency())
  };

  const getPlansItems = ():PlanItem[] => {
    try{return state.landing.plans.items}catch(e){return null}
  }

  const getLayoutSelectedPlan = (): number => {
    try {
      return state.landing.plans.items.find(el => el.preselected).id;
      
    } catch (e) {
      try{return state.landing.plans.items[0].id;}catch(e){console.log('Error getting preselected plan in useLanding > getLayoutSelectedPlan')}
    }
  }

  /** For landings we manage only the paper version adding as per UX
   * @TODO generalize
   */
  const getBundleChoosenConfigurableProducts = () => {
    if (!state.landing) return [];
    /** Forcing recalculating because the computed not works with choosenShippingMethod as this is an object (avoid to create another map). Maybe we can detach the modulesKeys/ModulesSkus mapping with this trick. */
    state.triggerChangeHelper;
    if (state.paperAdded.includes(state.layoutSelectedPlan) && state.choosenShippingMethod[state.layoutSelectedPlan]) {
      const bundle = state.landing.plans.items.find(el => el.id == state.layoutSelectedPlan);
      const paperOptionKey = Object.keys(bundle.deliveryCosts.costs).find(key => bundle.deliveryCosts.costs[key].uid == state.choosenShippingMethod[state.layoutSelectedPlan])
      return paperOptionKey ? [bundle.deliveryCosts.costs[paperOptionKey].sku]:[]
    } else {
      /**remove paper version */
      return []
    }
  }
  
  return {
    composableType: 'landing',
    landing: computed(() => state.landing),
    setLanding,
    plans: computed(() => state.landing ? state.landing.plans : null),
    getPlansLayout: () => state.landing.plansLayout,
    plansItems: computed(() => getPlansItems()),
    setPaperAdded: (val: {sku: string, active: boolean}) => setPaperAdded(state, val),
    paperAdded: computed(() => state.paperAdded),
    setChoosenShippingMethod: (id, val)=>setChoosenShippingMethod(state, id, val),
    choosenShippingMethod: computed(() => state.choosenShippingMethod),
    setSelectedShippingKey: (sku, skuValueIndex)=>setSelectedShippingKey(state, sku, skuValueIndex),
    selectedShippingKey: computed(() => state.selectedShippingKey),
    addModule: (val: GenericObjectType) => addModule(state, val),
    removeModule: (val: GenericObjectType) => removeModule(state, val),
    toggleModule: (val:GenericObjectType) => toggleModule(state, val),
    getChoosenModules: () => state.choosenModules,
    modulesKeys: computed(() => state.modulesKeys as Array<String>),
    setAcceptedQuantities: (qty: number) => setAcceptedQuantities(state, qty),
    emptyModules: ()=>emptyModules(state),
    layoutSelectedPlan: computed(()=>state.layoutSelectedPlan ?? getLayoutSelectedPlan()),
    setLayoutSelectedPlan,
    paymentFrequencyChoosen: computed(() => state.paymentFrequencyChoosen),
    setPaymentFrequencyChoosen : (val: string) => setPaymentFrequencyChoosen(state, getHighPaymentFrequency, val),
    selectedPlanLowPaymentFrequency : computed(()=>getLowPaymentFrequency()),
    selectedPlanHighPaymentFrequency: computed(() => getHighPaymentFrequency()),
    getField: (key: string) => state.landing[key],
    mainColor: computed(() => state.landing.mainColor),
    mainTextColor: computed(() => state.landing.mainTextColor),
    showPaymentFrequencySwitcher: computed(() => getLowPaymentFrequency() != getHighPaymentFrequency()),
    bundleChoosenConfigurableProducts: computed(() => getBundleChoosenConfigurableProducts()),
    hidePlansSelector: computed(() => state.landing.isSpecial || state.landing.plansLayout != PLANS_HORIZONTAL),
    selectedPlan : computed(()=>state.selectedPlan),
    revertOrderCard: computed(()=>state.landing.revertOrderCard),
    frequency_toggle: computed(()=>state.landing.frequency_toggle),
    landingEntryBillingPeriod: computed(()=>state.landing.landingEntryBillingPeriod),
    discounted_toggle_low_frequency: computed(()=>state.landing.discounted_toggle_low_frequency),
    discounted_toggle_high_frequency: computed(()=>state.landing.discounted_toggle_high_frequency),
    verticalMobileVersion: computed(()=>state.landing.verticalMobileVersion),
    labelDescriptionFooter: computed(()=>state.landing?.labelDescriptionFooter),
    textHighlighted: computed(()=>state.landing.textHighlighted)
  }
}

export function resetLandingModalState() {
  state.paperAdded = [];
  state.choosenShippingMethod = {};
  state.selectedShippingKey = {};
  state.choosenModules = new Map();
}

//selectedPlanLowPaymentFrequency!=selectedPlanHighPaymentFrequency
export default useLanding;
export * from './useLanding';
